/**
 * VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_
 * FOUNDATION_P0_V1 — "营销活动" tab (task spec §11, PromotionCampaign).
 *
 * ENABLE/DISABLE is a manual `status` toggle; expiration is automatic
 * and derived (`is_active_now`, backend `PromotionCampaign.is_active_
 * now()` — a campaign past its own `end_at` is never "active" again,
 * regardless of `status`), never a second manual "expired" flag to keep
 * in sync.
 */
import { Alert, Button, DatePicker, Drawer, Form, Input, InputNumber, Select, Table, Tag, Typography, message } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { platformCreatePromotionCampaign, platformListBillingPlans, platformListPromotionCampaigns, platformUpdatePromotionCampaign } from '../../api'
import type { BillingPlan, PromotionCampaign } from '../../types'
import { SectionCard } from '../adminShared'
import { ACTIVE_CURRENCIES, errMsg, formatDateTimeShort } from './billingShared'

export default function PromotionTab() {
  const [campaigns, setCampaigns] = useState<PromotionCampaign[]>([])
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<PromotionCampaign | null>(null)

  const load = () => {
    setLoading(true)
    setError(null)
    Promise.all([platformListPromotionCampaigns(), platformListBillingPlans()])
      .then(([c, p]) => {
        setCampaigns(c.data)
        setPlans(p.data)
      })
      .catch((err) => setError(errMsg(err, '加载营销活动失败')))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const toggleStatus = async (row: PromotionCampaign) => {
    const nextStatus = row.status === 'ENABLED' ? 'DISABLED' : 'ENABLED'
    try {
      const res = await platformUpdatePromotionCampaign(row.id, { status: nextStatus })
      setCampaigns((prev) => prev.map((c) => (c.id === row.id ? res.data : c)))
    } catch (err) {
      message.error(errMsg(err, '更新失败'))
    }
  }

  const planLabel = (keys: string[] | null) => {
    if (!keys || keys.length === 0) return '全部套餐'
    return keys.map((k) => plans.find((p) => p.key === k)?.display_name ?? k).join(' / ')
  }

  return (
    <div>
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />}
      <SectionCard
        title="营销活动（Promotion Campaign）"
        extra={
          <Button
            type="primary"
            size="small"
            onClick={() => {
              setEditing(null)
              setOpen(true)
            }}
          >
            + 新增活动
          </Button>
        }
      >
        <Typography.Paragraph type="secondary" style={{ margin: '0 4px 10px', fontSize: 12 }}>
          限时营销活动（例如「圣诞积分活动：2026-12-20 → 2026-12-31，充值满 ¥500 赠送 20%」）。活动过期后自动失效（is_active_now），无需手动清理。
        </Typography.Paragraph>
        <Table
          className="pf-data-table"
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={campaigns}
          pagination={false}
          columns={[
            { title: '名称', dataIndex: 'name', width: 160 },
            { title: 'Key', dataIndex: 'key', width: 140, render: (v: string) => <code>{v}</code> },
            { title: '开始', dataIndex: 'start_at', width: 150, render: (v: string) => formatDateTimeShort(v) },
            { title: '结束', dataIndex: 'end_at', width: 150, render: (v: string) => formatDateTimeShort(v) },
            {
              title: '触发条件',
              width: 160,
              render: (_: unknown, row: PromotionCampaign) =>
                row.trigger_min_amount_minor != null ? `充值满 ${(row.trigger_min_amount_minor / 100).toFixed(2)}` : row.trigger_type,
            },
            {
              title: '奖励',
              width: 140,
              render: (_: unknown, row: PromotionCampaign) => (row.reward_type === 'PERCENTAGE' ? `+${row.reward_value}%` : `+${row.reward_value} 积分`),
            },
            { title: '适用套餐', dataIndex: 'applicable_plan_keys', width: 140, render: (v: string[] | null) => planLabel(v) },
            { title: '适用币种', dataIndex: 'applicable_currencies', width: 110, render: (v: string[] | null) => (v && v.length ? v.join('/') : '全部') },
            {
              title: '状态',
              width: 130,
              render: (_: unknown, row: PromotionCampaign) => (
                <>
                  <Tag color={row.status === 'ENABLED' ? 'green' : 'default'}>{row.status}</Tag>
                  {row.is_active_now && <Tag color="blue">进行中</Tag>}
                </>
              ),
            },
            {
              title: '操作',
              width: 140,
              render: (_: unknown, row: PromotionCampaign) => (
                <>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => {
                      setEditing(row)
                      setOpen(true)
                    }}
                  >
                    编辑
                  </Button>
                  <Button size="small" type="link" onClick={() => void toggleStatus(row)}>
                    {row.status === 'ENABLED' ? '停用' : '启用'}
                  </Button>
                </>
              ),
            },
          ]}
        />
      </SectionCard>

      <PromotionDrawer
        open={open}
        editing={editing}
        plans={plans}
        onClose={() => setOpen(false)}
        onCreated={(c) => setCampaigns((prev) => [...prev, c])}
        onUpdated={(c) => setCampaigns((prev) => prev.map((x) => (x.id === c.id ? c : x)))}
      />
    </div>
  )
}

function PromotionDrawer({
  open,
  editing,
  plans,
  onClose,
  onCreated,
  onUpdated,
}: {
  open: boolean
  editing: PromotionCampaign | null
  plans: BillingPlan[]
  onClose: () => void
  onCreated: (c: PromotionCampaign) => void
  onUpdated: (c: PromotionCampaign) => void
}) {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    if (editing) {
      form.setFieldsValue({
        name: editing.name,
        date_range: [dayjs(editing.start_at), dayjs(editing.end_at)],
        applicable_plan_keys: editing.applicable_plan_keys ?? undefined,
        applicable_currencies: editing.applicable_currencies ?? undefined,
        trigger_min_amount: editing.trigger_min_amount_minor != null ? editing.trigger_min_amount_minor / 100 : undefined,
        reward_type: editing.reward_type,
        reward_value: Number(editing.reward_value),
      })
    } else {
      form.resetFields()
      form.setFieldsValue({ trigger_type: 'TOPUP_MIN_AMOUNT', reward_type: 'PERCENTAGE' })
    }
  }, [open, editing, form])

  const submit = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    try {
      if (editing) {
        const res = await platformUpdatePromotionCampaign(editing.id, {
          name: values.name,
          start_at: values.date_range[0].toISOString(),
          end_at: values.date_range[1].toISOString(),
          applicable_plan_keys: values.applicable_plan_keys?.length ? values.applicable_plan_keys : null,
          applicable_currencies: values.applicable_currencies?.length ? values.applicable_currencies : null,
          trigger_min_amount_minor: values.trigger_min_amount != null ? Math.round(values.trigger_min_amount * 100) : null,
          reward_type: values.reward_type,
          reward_value: values.reward_value,
        })
        onUpdated(res.data)
        message.success('活动已更新')
      } else {
        const res = await platformCreatePromotionCampaign({
          key: values.key,
          name: values.name,
          start_at: values.date_range[0].toISOString(),
          end_at: values.date_range[1].toISOString(),
          applicable_plan_keys: values.applicable_plan_keys?.length ? values.applicable_plan_keys : null,
          applicable_currencies: values.applicable_currencies?.length ? values.applicable_currencies : null,
          trigger_type: 'TOPUP_MIN_AMOUNT',
          trigger_min_amount_minor: values.trigger_min_amount != null ? Math.round(values.trigger_min_amount * 100) : null,
          reward_type: values.reward_type,
          reward_value: values.reward_value,
        })
        onCreated(res.data)
        message.success('活动已创建')
      }
      onClose()
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Drawer title={editing ? '编辑营销活动' : '新增营销活动'} open={open} onClose={onClose} width={460} destroyOnClose>
      <Form form={form} layout="vertical">
        {!editing && (
          <Form.Item
            name="key"
            label="Campaign Key"
            extra="创建后不可修改"
            rules={[{ required: true }, { pattern: /^[a-z][a-z0-9_]*$/, message: '仅支持小写字母、数字、下划线' }]}
          >
            <Input placeholder="例如：xmas_2026" />
          </Form.Item>
        )}
        <Form.Item name="name" label="活动名称" rules={[{ required: true }]}>
          <Input placeholder="例如：圣诞积分活动" />
        </Form.Item>
        <Form.Item name="date_range" label="活动时间" rules={[{ required: true }]}>
          <DatePicker.RangePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="trigger_min_amount" label="触发条件：充值满（金额）">
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="reward_type" label="奖励方式" rules={[{ required: true }]}>
          <Select
            options={[
              { label: '按百分比（PERCENTAGE）', value: 'PERCENTAGE' },
              { label: '固定积分（FIXED_CREDITS）', value: 'FIXED_CREDITS' },
            ]}
          />
        </Form.Item>
        <Form.Item name="reward_value" label="奖励数值" rules={[{ required: true }]}>
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="applicable_plan_keys" label="适用套餐（留空为全部）">
          <Select mode="multiple" options={plans.map((p) => ({ label: p.display_name, value: p.key }))} allowClear />
        </Form.Item>
        <Form.Item name="applicable_currencies" label="适用币种（留空为全部）">
          <Select mode="multiple" options={ACTIVE_CURRENCIES.map((c) => ({ label: c, value: c }))} allowClear />
        </Form.Item>
      </Form>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose}>取消</Button>
        <Button type="primary" loading={submitting} onClick={() => void submit()}>
          {editing ? '保存' : '创建'}
        </Button>
      </div>
    </Drawer>
  )
}
